import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { getMerchantGmtString, getTextColorByBgColor, isWhite, lightenDarkenColor } from 'common-logic';
import {
  DeliverySlot,
  MerchantBranding,
  MerchantDetails,
  PromotionBranding,
  PromotionService,
  PromotionType,
  SocialMediaLink,
  TrackingInfo,
  WatchlistService,
} from '@evermile/customer-tracking';
import { animate, keyframes, query, stagger, style, transition, trigger } from '@angular/animations';
import { Address } from '@evermile/customer-tracking/model/address';
import { HttpClient } from '@angular/common/http';
import { fixExternalAddress } from '../utils/addressUtils';
import { ActivatedRoute } from '@angular/router';
import { Coordinate, MapMarker, MapMarkerType, vehicleTypeToTrackingIconType } from '../utils/mapUtils';
import CustomerTrackingStatusEnum = TrackingInfo.CustomerTrackingStatusEnum;
import { OrderStatusTrackingComponent } from './components/order-status-tracking/order-status-tracking.component';
import { GtmTracking } from '../services/gtm.service';
import {
  FabPromotionProductsComponent,
  PromotionDetails,
} from './components/fab-promotion-products/fab-promotion-products.component';
import { AddressType } from '@evermile/evermile_internal_order_manager';
import { EditField, TimeWindow } from '@evermile/api';
import { DatePipe } from '@angular/common';
import { EditOrderService } from '../services/edit-order.service';
import { Subscription } from 'rxjs';
import {
  FailedDeliveryRebookFlowComponent,
  RebookDeliveryOption,
} from '../failed-delivery-rebook-flow/failed-delivery-rebook-flow.component';
import { WatchlistTaskUpdateStatus, WatchlistTaskUpdateType } from '@evermile/customer-tracking';

const DefaultBgColor = '#4B3BFF';
const pageHeaderHeight = 65;
const scrollPadding = 10;

function typeToOrder(type: SocialMediaLink.TypeEnum): number {
  switch (type) {
    case SocialMediaLink.TypeEnum.Facebook:
      return 1;
    case SocialMediaLink.TypeEnum.Instagram:
      return 2;
    case SocialMediaLink.TypeEnum.Tiktok:
      return 3;
    case SocialMediaLink.TypeEnum.Youtube:
      return 4;
    default:
      return 10;
  }
}

const showPickupLocationStatuses = [CustomerTrackingStatusEnum.Ordered, CustomerTrackingStatusEnum.OnItsWay];

export const TrackingFinalStatuses = [
  CustomerTrackingStatusEnum.Delivered,
  CustomerTrackingStatusEnum.Cancelled,
  CustomerTrackingStatusEnum.ReturnedToSender,
  CustomerTrackingStatusEnum.ReturningToSender,
  CustomerTrackingStatusEnum.Failed,
];

export const TrackingLiveStatuses = [
  CustomerTrackingStatusEnum.OnItsWay,
  CustomerTrackingStatusEnum.DeliveredToCollectionPoint,
];

const changedKeyToScrollElementId: { [key: string]: string } = {
  socialMediaLinks: 'social-media-links',
  websiteUrl: 'website-url',
  googleReviewsUrl: 'google-reviews-url',
  promotionBanner: 'promotion-banner',
  enablePromotionBanner: 'promotion-banner',
  instagramPromotionData: 'instagram-promotion',
  enableProductsPromotion: 'products-promotion',
  promotionProductsData: 'products-promotion',
};

@Component({
  selector: 'common-customer-order-tracking',
  templateUrl: './customer-order-tracking.component.html',
  styleUrls: ['./customer-order-tracking.component.scss'],
  animations: [
    trigger('pageAnimations', [
      transition(':enter', [
        query('.fade-in', [
          style({ opacity: 0 }),
          stagger(100, [animate('500ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1 }))]),
        ]),
      ]),
    ]),
    trigger('fadeInOutTrigger', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('300ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 1 })),
      ]),
      transition(':leave', [animate('300ms cubic-bezier(0.35, 0, 0.25, 1)', style({ opacity: 0 }))]),
    ]),
    trigger('gradientTrigger', [
      transition('0=>1', [
        animate(
          '1s ease-out',
          keyframes([
            style({ backgroundPosition: '0% 50%' }),
            style({ backgroundPosition: '100% 50%' }),
            style({ backgroundPosition: '0% 50%' }),
          ]),
        ),
      ]),
      transition('*=>2', [
        animate(
          '100ms ease-in',
          keyframes([style({ backgroundColor: '{{startColor}}' }), style({ backgroundColor: '{{endColor}}' })]),
        ),
      ]),
    ]),
  ],
})
export class CustomerOrderTrackingComponent implements OnChanges, OnInit, OnDestroy {
  @Input() isPreview = false;
  @Input() trackingPrefix?: string;
  @Input() hideEvermileLogo = false;
  @Input() addLogoPlaceHolder = false;
  @Input() orderDetails?: TrackingInfo;
  @Input() facebookClientToken?: string;
  @Input() isMobilePreviewOpen = false;
  @Input() brandingInfo?: MerchantBranding;
  @Input() merchantDetails?: MerchantDetails;
  @Input() loadingState = true;
  @Input() errorState = false;
  @Input() contained?: boolean;
  @Input() elementForceScroll?: string;
  @Input() preventScroll = false;
  @Input({ required: true }) isDevEnvironment = true;
  @ViewChild('container') container!: ElementRef;
  @ViewChild('title') titleElement!: ElementRef;
  @Output() promotionFabDisplayChange = new EventEmitter<
    'invisible' | 'minimized-large' | 'minimized-small' | 'open'
  >();
  @ViewChild('fabPromotionProducts') fabPromotionProducts!: FabPromotionProductsComponent;
  @ViewChild('promotionProductsCard') promotionProductsCard!: ElementRef;
  @ViewChild('orderStatusTracking') orderStatusTracking!: OrderStatusTrackingComponent;
  @ViewChild('failedDeliveryRebookFlow') failedDeliveryRebookFlow!: FailedDeliveryRebookFlowComponent;
  background = '';
  elementsBackground = '';
  mapIconsBackground = '';
  mapCardExpanded = false;
  elementsText = '';
  backgroundSize = '';
  textColor = '#fff';
  editCtaFontWeight?: string;
  gradientState = { value: 0, params: { startColor: this.background, endColor: this.background } };
  brighterColor = '';
  darkerColor?: string;
  showCourierDetails = false;
  showProofOfDelivery = false;
  showContactMerchant = false;
  showSocialLinks = false;
  websiteUrlText = '';
  logoLoaded = false;
  contentVisible = false;
  contentReady = false;
  overflowHidden = false;
  oEmbedDisplayError?: string;
  driverLocation?: Coordinate;
  mapMarkers?: MapMarker[];
  readonly StatusEnum = CustomerTrackingStatusEnum;

  readonly SocialMediaType = SocialMediaLink.TypeEnum;
  scrolled = false;
  promotionProductsVisible = false;
  promotionProductsScrollState = {
    shouldCollapse: false,
    shouldHide: false,
  };
  wrapTitle = false;
  merchantNameDisplayBlock = true;
  displayTitle = false;

  dropoffStart?: string;
  dropoffEnd?: string;
  dropoffEta?: string;
  commitedDropoffInfoText?: string;
  address?: Address;
  showInstagramEmbed: any = false;
  promotionBranding: PromotionBranding = {};
  isSenderTracking = false;
  isGiftRecipientTracking = false;
  shouldHidePromotions? = false;
  embedded?: boolean;
  isAdmin = false;
  centeredLogo = false;
  taskIdToRebookOrder?: string;

  showRebookOrder = false;
  showRebookOrderButton = false;
  disableRebookButton = true;

  //PromoCodes
  productsPromotionDetails?: PromotionDetails;

  protected readonly AddressType = AddressType;

  rebookFlowSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private editOrderService: EditOrderService,
    private watchlistService: WatchlistService,
    private promotionService: PromotionService,
    private datePipe: DatePipe,
    private http: HttpClient,
    private gtmTracking: GtmTracking,
  ) {}

  ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      if (params && params['embedded']) {
        this.embedded = params['embedded'].toLowerCase() === 'true';
      }
      if (params && params['admin']) {
        this.isAdmin = params['admin'].toLowerCase() === 'true';
      }
    });
    this.setPromotionCodes();
    this.rebookFlowSub = this.editOrderService.openRebookFlow$.subscribe(() => {
      // TODO: mile feature block
      if (this.isDevEnvironment) {
        this.showRebookOrder = true;
      }
    });
  }

  ngOnDestroy() {
    this.rebookFlowSub?.unsubscribe();
  }

  setPromotionCodes() {
    if (!this.contained && this.brandingInfo?.enableProductsPromotion) {
      const promotionType = this.brandingInfo.enableEditOrderAddProductsPromotion
        ? PromotionType.EditOrderAddProducts
        : PromotionType.FeaturedProducts;

      this.promotionService.promotionsTypeGet(promotionType).subscribe((data) => {
        this.productsPromotionDetails = {
          ...data,
          expiresAt: data.expiresAt ? new Date(data.expiresAt) : undefined,
        };
      });
    }
  }

  getFacebookPage(url: string) {
    if (!this.facebookClientToken || this.merchantDetails?.id !== 'dc7e6855-91f3-4f4b-9851-2ba74f29cb41') return;
    this.http
      .get<any>(`https://graph.facebook.com/v17.0/instagram_oembed?url=${url}&access_token=${this.facebookClientToken}`)
      .subscribe(
        (response) => {},
        (error) => {
          this.oEmbedDisplayError = error.error.error.message;
        },
      );
  }

  gradientAnimationDone(event: any) {
    if (this.gradientState.value == 2) {
      this.contentReady = true;
      return;
    }
    if (!this.loadingState) {
      this.contentVisible = true;
      this.setView();
      this.gradientState = {
        value: 2,
        params: { startColor: this.gradientState.value == 0 ? '#e73c7e' : '#23a6d5', endColor: this.background },
      };
      return;
    }
    if (this.gradientState.value == 0 || this.gradientState.value == 1) {
      this.gradientState = {
        value: 1 - this.gradientState.value,
        params: { startColor: this.background, endColor: this.background },
      };
    }
  }

  private setView() {
    if (!this.contentVisible) {
      return;
    }
    if (this.brandingInfo && this.orderDetails && this.merchantDetails) {
      this.isSenderTracking = this.orderDetails.tokenType === 'sender';
      this.isGiftRecipientTracking = !!this.orderDetails.isGift && !this.isSenderTracking;
      this.shouldHidePromotions =
        this.isGiftRecipientTracking &&
        this.orderDetails.customerTrackingStatus !== CustomerTrackingStatusEnum.Delivered;
      this.background = this.shouldHidePromotions ? '#FFF3F3' : this.brandingInfo.primaryColor || DefaultBgColor;
      this.elementsBackground = isWhite(this.background) ? '#343E57' : this.background;
      this.mapIconsBackground = isWhite(this.background) ? '#4B3BFF' : this.background;
      this.backgroundSize = 'auto';
      ((this.brandingInfo.logoUrl = this.brandingInfo.logoUrl
        ? this.brandingInfo.logoUrl + '?t=' + new Date().getTime()
        : undefined),
        (this.textColor = getTextColorByBgColor(this.background)));
      this.elementsText = isWhite(this.background) ? '#FFFFFF' : this.textColor;
      this.editCtaFontWeight = this.brandingInfo?.editCtaFontWeight || '550';
      this.brighterColor = isWhite(this.background) ? '#FAFBFC' : lightenDarkenColor(this.background, 20);
      this.darkerColor = lightenDarkenColor(this.background, -30);
      this.setCustomizedStyleForMerchant();
      this.showCourierDetails =
        this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.OnItsWay &&
        !!this.orderDetails.trackingInfo.courierName;
      if (
        (this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.Delivered ||
          this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.Failed ||
          this.orderDetails.customerTrackingStatus === CustomerTrackingStatusEnum.ReturnedToSender) &&
        ((this.orderDetails.deliveryNotes && this.orderDetails.deliveryNotes.length) ||
          (this.orderDetails.proofOfDelivery && this.orderDetails.proofOfDelivery.length))
      ) {
        this.showProofOfDelivery = true;
      }
      if (this.brandingInfo.websiteUrl) {
        this.websiteUrlText = this.brandingInfo.websiteUrl;
        if (this.websiteUrlText.includes('?')) {
          this.websiteUrlText = this.websiteUrlText.slice(0, this.websiteUrlText.indexOf('?'));
        }
        if (this.websiteUrlText.endsWith('/')) {
          this.websiteUrlText = this.websiteUrlText.slice(0, -1);
        }
        if (this.websiteUrlText.startsWith('https')) {
          this.websiteUrlText = this.websiteUrlText.slice(8);
        } else if (this.websiteUrlText.startsWith('http')) {
          this.websiteUrlText = this.websiteUrlText.slice(7);
        }
      }
      if (this.brandingInfo.socialMediaLinks && this.brandingInfo.socialMediaLinks.length) {
        this.brandingInfo.socialMediaLinks.sort((a: SocialMediaLink, b: SocialMediaLink) => {
          return typeToOrder(a.type) - typeToOrder(b.type);
        });
        this.showSocialLinks = true;
      }
      this.showContactMerchant =
        (this.brandingInfo.allowContactPhone || this.brandingInfo.allowContactEmail) &&
        this.orderDetails.customerTrackingStatus !== CustomerTrackingStatusEnum.Failed;
      const isExternal =
        this.orderDetails.isExternalPickup && this.orderDetails.originAddress.type !== AddressType.Pudo;
      this.dropoffStart = isExternal
        ? this.orderDetails.trackingInfo.pickupWindowStart
        : this.orderDetails.trackingInfo.dropoffWindowStart;
      this.dropoffEnd = isExternal
        ? this.orderDetails.trackingInfo.pickupWindowEnd
        : this.orderDetails.trackingInfo.dropoffWindowEnd;
      this.dropoffEta = isExternal
        ? this.orderDetails.trackingInfo.pickupEta
        : this.orderDetails.trackingInfo.dropoffEta;
      this.commitedDropoffInfoText =
        (this.orderDetails.customerTrackingStatus === this.StatusEnum.OnItsWay ||
          this.orderDetails.customerTrackingStatus === this.StatusEnum.Ordered) &&
        this.isDropoffTimeDifferentThanOriginalDropoffTime()
          ? this.getCommittedDropoffInfoText(this.orderDetails.communicatedDropoff)
          : undefined;
      this.address = isExternal
        ? fixExternalAddress(this.orderDetails.originAddress)
        : this.orderDetails.destinationAddress;
      this.showInstagramEmbed = this.merchantDetails.id === 'dc7e6855-91f3-4f4b-9851-2ba74f29cb41';
      if (this.showInstagramEmbed && this.facebookClientToken)
        this.getFacebookPage('https://www.instagram.com/evermile.io');
      this.promotionBranding =
        (this.isGiftRecipientTracking ? this.brandingInfo.giftBranding : this.brandingInfo) || {};
      this.brandingInfo.enableTrackingMap && this.setMapMarkers();
      this.setInitialPromotionProductsScrollState();
    }
    if (!this.errorState) {
      this.calculateTitleLines();
    }
  }

  isDropoffTimeDifferentThanOriginalDropoffTime() {
    return (
      this.dropoffEta ||
      this.dropoffStart !== this.orderDetails!.communicatedDropoff?.start ||
      this.dropoffEnd !== this.orderDetails!.communicatedDropoff?.end
    );
  }

  getCommittedDropoffInfoText(window?: TimeWindow | DeliverySlot): string | undefined {
    if (!window?.start || !window?.end) return;
    return `Committed drop-off window: ${this.getHours(new Date(window.start).toString())}-${this.getHours(new Date(window.end).toString())}`;
  }

  getHours(date: string): string | undefined {
    return this.datePipe
      .transform(date, 'h:mmaaa', getMerchantGmtString(this.merchantDetails!.timezoneOffset || 0))
      ?.toLocaleLowerCase();
  }

  private setCustomizedStyleForMerchant() {
    // Ottolenghi
    if (this.brandingInfo?.brandId === 'l8pfqc5y') {
      this.centeredLogo = true;
      this.elementsBackground = '#E51636';
      this.mapIconsBackground = '#E51636';
    }
  }

  private calculateTitleLines() {
    setTimeout(() => {
      const origWidth = this.titleElement?.nativeElement?.offsetWidth;
      this.wrapTitle = true;
      this.cdr.detectChanges();
      if (origWidth > this.titleElement?.nativeElement?.offsetWidth) {
        this.merchantNameDisplayBlock = false;
      }
      this.displayTitle = true;
    }, 200);
  }

  private setInitialPromotionProductsScrollState() {
    setTimeout(() => {
      this.calcPromotionProductsScrollState();
    }, 200);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.errorState) {
      return;
    }
    if (changes['isMobilePreviewOpen'] && this.isMobilePreviewOpen && this.elementForceScroll) {
      this.scrollToElementById(changedKeyToScrollElementId[this.elementForceScroll], pageHeaderHeight);
    }
    if (changes['brandingInfo']) {
      this.setPromotionCodes();
      if (!changes['brandingInfo'].firstChange) {
        const curr = changes['brandingInfo'].currentValue;
        const prev = changes['brandingInfo'].previousValue;
        if (curr && prev) {
          const changedKeys = Object.keys(curr).filter(
            (key) => JSON.stringify(curr[key]) !== JSON.stringify(prev[key]),
          );
          if (changedKeys.length == 1 || this.elementForceScroll) {
            const forceElement = this.getForcedElementForSocialSettings(changes['brandingInfo'].currentValue);
            const changedKey = forceElement || changedKeys[0];

            if (changedKey === 'giftBranding') {
              const giftChangedKeys = Object.keys(curr['giftBranding']).filter(
                (key) => JSON.stringify(curr['giftBranding'][key]) !== JSON.stringify(prev['giftBranding'][key]),
              );

              if (giftChangedKeys.length && changedKeyToScrollElementId[giftChangedKeys[0]]) {
                this.scrollToElementById(changedKeyToScrollElementId[giftChangedKeys[0]], 0);
              }
            }

            if (changedKeyToScrollElementId[changedKey]) {
              if (changedKey === 'socialMediaLinks') {
                const currKeys = Object.keys(curr?.['socialMediaLinks'] || {})?.sort();
                const prevKeys = Object.keys(prev?.['socialMediaLinks'] || {})?.sort();
                if (JSON.stringify(currKeys) !== JSON.stringify(prevKeys)) {
                  this.scrollToElementById(changedKeyToScrollElementId[changedKey], pageHeaderHeight);
                }
              } else {
                this.scrollToElementById(changedKeyToScrollElementId[changedKey], pageHeaderHeight);
              }
            }
          }
        }
        this.setView();
      }
    } else if (changes['orderDetails']) {
      this.setView();
    }
    if (changes['loadingState'] && !this.loadingState) {
      this.route.queryParams.subscribe((params) => {
        // TODO: mile feature block
        if (this.isDevEnvironment) {
          if (params && params['tid-rebook']) {
            this.taskIdToRebookOrder = params['tid-rebook'];
            this.showRebookOrder =
              (!!this.taskIdToRebookOrder &&
                this.orderDetails?.customerTrackingStatus === CustomerTrackingStatusEnum.Failed) ||
              this.orderDetails?.customerTrackingStatus === CustomerTrackingStatusEnum.ReturnedToSender ||
              this.orderDetails?.customerTrackingStatus === CustomerTrackingStatusEnum.ReturningToSender;
            this.showRebookOrderButton = this.showRebookOrder;
            if (!this.taskIdToRebookOrder) return;
            this.watchlistService
              .postTaskValidate(this.taskIdToRebookOrder, { updateType: WatchlistTaskUpdateType.ProposalSelection })
              .subscribe({
                next: (res) => {
                  this.disableRebookButton = !res.allowed;
                },
                error: () => {
                  this.disableRebookButton = true;
                },
              });
          }
        }
      });
    }
  }

  getForcedElementForSocialSettings(changes: any) {
    if (this.elementForceScroll === 'instagramPromotionData' && changes['websiteUrl']) {
      return 'websiteUrl';
    }
    if (this.elementForceScroll === 'instagramPromotionData' && changes['socialMediaLinks']) {
      return 'socialMediaLinks';
    }
    return this.elementForceScroll;
  }

  private scrollToElementById(id: string, headerPadding: number) {
    if (this.preventScroll) return;
    setTimeout(() => {
      const element = this.container.nativeElement.querySelector(`#${id}`);
      if (element) {
        this.container?.nativeElement.scrollTo({
          top: element.offsetTop - headerPadding - scrollPadding + 120,
          behavior: 'smooth',
        });
      }
    }, 500);
  }

  private setMapMarkers() {
    if (!this.orderDetails) {
      return;
    }
    const currentDriverLocation = this.getCurrentDriverLocation();
    const driverMarker = this.mapMarkers?.find((m) => m.id === 'driver');
    const pickupMarker = this.mapMarkers?.find((m) => m.id === 'pickup');
    const dropoffMarker = this.mapMarkers?.find((m) => m.id === 'dropoff');

    const shouldDrawDriver =
      this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.OnItsWay &&
      this.orderDetails.shouldShowLiveCourierLocation &&
      this.dropoffEta &&
      this.getDiffMinutesFromNow(this.dropoffEta) <= 30;

    if (
      this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.OnItsWay &&
      this.driverLocation &&
      driverMarker
    ) {
      if (
        currentDriverLocation &&
        (this.driverLocation?.lng !== currentDriverLocation.lng ||
          this.driverLocation.lat !== currentDriverLocation.lat)
      ) {
        this.orderStatusTracking?.mapComponent.moveMarker('driver', currentDriverLocation);
      }
      this.driverLocation = currentDriverLocation;
    } else if (
      (!pickupMarker && !dropoffMarker) ||
      (currentDriverLocation && !this.driverLocation && shouldDrawDriver) ||
      (pickupMarker && !showPickupLocationStatuses.includes(this.orderDetails.customerTrackingStatus))
    ) {
      const markers: MapMarker[] = [];

      if (shouldDrawDriver) {
        this.driverLocation = currentDriverLocation;
        this.driverLocation &&
          markers.push({
            id: 'driver',
            type: this.getDriverIconType(),
            coordinate: this.driverLocation,
            color: this.mapIconsBackground,
          });
      }

      const pickupLocation = this.orderDetails.originAddress.geoLocation;
      const destinationLocation = this.orderDetails.destinationAddress.geoLocation;
      if (showPickupLocationStatuses.includes(this.orderDetails.customerTrackingStatus)) {
        if (!this.shouldHidePromotions) {
          pickupLocation &&
            markers.push({
              id: 'pickup',
              type: MapMarkerType.pickup,
              coordinate: pickupLocation,
            });
        }

        destinationLocation &&
          markers.push({
            id: 'dropoff',
            type: MapMarkerType.dropoff,
            coordinate: destinationLocation,
          });
      } else if (
        this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.Delivered ||
        this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.Collected
      ) {
        destinationLocation &&
          markers.push({
            id: 'dropoff',
            type: MapMarkerType.dropoffSuccess,
            coordinate: destinationLocation,
          });
      }

      this.mapMarkers = markers;

      setTimeout(() => {
        if (
          this.orderDetails &&
          this.orderDetails.customerTrackingStatus == CustomerTrackingStatusEnum.OnItsWay &&
          this.driverLocation &&
          destinationLocation
        ) {
          this.orderStatusTracking.mapComponent.fitMapBounds([this.driverLocation, destinationLocation]);
        }
      }, 3000);
    }
  }

  getCurrentDriverLocation() {
    return this.orderDetails?.trackingInfo.courierLocation
      ? {
          lat: this.orderDetails.trackingInfo.courierLocation.lat,
          lng: this.orderDetails.trackingInfo.courierLocation.lng,
        }
      : undefined;
  }

  onScroll(event: any) {
    this.scrolled = event.target.scrollTop > 20;
    this.calcPromotionProductsScrollState();
  }

  calcPromotionProductsScrollState() {
    if (typeof this.promotionProductsCard?.nativeElement.getBoundingClientRect().y !== 'undefined') {
      const clientRect = this.promotionProductsCard?.nativeElement.getBoundingClientRect();
      this.promotionProductsScrollState = {
        shouldHide: clientRect.y < window.innerHeight - clientRect.height - 30,
        shouldCollapse: clientRect.y < window.innerHeight - 60,
      };
    }
  }

  scrollToProductsCard() {
    this.promotionProductsCard.nativeElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  private getDriverIconType() {
    if (this.orderDetails?.trackingInfo.vehicleType) {
      return vehicleTypeToTrackingIconType[this.orderDetails.trackingInfo.vehicleType];
    } else return MapMarkerType.car;
  }

  toggleMapCardFullScreen(expand: boolean) {
    this.mapCardExpanded = expand;
    this.gtmTracking.clickEvent(
      `${this.trackingPrefix || ''}Tracking Page/${this.orderDetails?.customerTrackingStatus}/Expanded Map ${expand ? 'opened' : 'closed'}`,
    );
  }

  toggleRebookFullScreen(expand: boolean) {
    this.showRebookOrder = expand;
    // this.gtmTracking.clickEvent(`${this.trackingPrefix||''}Tracking Page/${this.orderDetails?.customerTrackingStatus}/Expanded Map ${expand ? 'opened' : 'closed'}`)
  }

  overlayKeydown($event: KeyboardEvent) {
    if ($event.code.toLowerCase() === 'escape') {
      this.toggleMapCardFullScreen(false);
    }
  }

  overlayRebookKeyDown($event: KeyboardEvent) {
    if ($event.code.toLowerCase() === 'escape') {
      this.showRebookOrder = false;
    }
  }

  private getDiffMinutesFromNow(time: string) {
    const diffMillis = new Date(time).getTime() - new Date().getTime();
    return Math.round(diffMillis / 60000);
  }

  onSelectRebookOption(option: RebookDeliveryOption) {
    //this.gtmTracking.clickEvent(`${this.trackingPrefix||''}Tracking Page/${this.orderDetails?.customerTrackingStatus}/Rebook Order`)
    if (!this.taskIdToRebookOrder) return;
    this.editOrderService.selectRebookOption(this.taskIdToRebookOrder || '', option.proposalId).subscribe({
      next: (res) => {
        if (res.status === WatchlistTaskUpdateStatus.UpdateReceived) {
          this.failedDeliveryRebookFlow.confirmTimSlotUpdated();
        } else if (res.status === WatchlistTaskUpdateStatus.UpdateBlocked) {
          this.failedDeliveryRebookFlow.showErrorMessage();
        }
      },
      error: (err) => {
        this.failedDeliveryRebookFlow.showErrorMessage();
      },
    });
  }

  protected readonly EditField = EditField;
}
